import React from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import { Radio } from 'components/DesignSystem/Radio';
import { CustomizedFormErrorMessage } from 'components/common';
import classes from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/LinkedActivities/LinkedActivities.module.scss';
import {
    getFormError,
    requiredWhenSelected,
} from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/LinkedActivities/LinkedActivities.utils';
import { ActivityCodesSelect } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/LinkedActivities/components/ActivityCodesSelect/ActivityCodesSelect';
import { ClusterSelect } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/LinkedActivities/components/ClusterSelect/ClusterSelect';
import { SegmentSelect } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/LinkedActivities/components/SegmentSelect/SegmentSelect';
import { SpecificActivitiesSelect } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/LinkedActivities/components/SpecificActivitiesSelect/SpecificActivitiesSelect';
import { WbsSelect } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/LinkedActivities/components/WbsSelect/WbsSelect';
import { FormProps } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/UpdateRiskRegisterModal/components/UpdateRiskRegisterForm/UpdateRiskRegisterForm';

const LINK_OPTION_ORDER: FormProps['linkOption'][] = [
    'specific',
    'wbs',
    'activityCodes',
    'cluster',
    'segment',
    'all',
];

export const LinkedActivities = () => {
    const methods = useFormContext<FormProps>();

    const linkOption = methods.watch('linkOption');

    const {
        control,
        formState: { errors },
    } = methods;

    const setLinkOption = (value: FormProps['linkOption']) => {
        methods.setValue('linkOption', value, { shouldDirty: true });
    };

    const formError = getFormError(linkOption, errors);
    const selectedRowIndex = LINK_OPTION_ORDER.indexOf(linkOption);
    const hasInput = selectedRowIndex >= 0 && linkOption !== 'all';

    return (
        <div className={classes.formSection}>
            <div className={classes.optionColumn}>
                <div className={classes.optionRow}>
                    <Radio
                        name={'linkOption'}
                        value={'specific'}
                        checked={linkOption === 'specific'}
                        onChange={() => setLinkOption('specific')}
                        label={'Link specific tasks'}
                    />
                </div>

                <div className={classes.optionRow}>
                    <Radio
                        name={'linkOption'}
                        value={'wbs'}
                        checked={linkOption === 'wbs'}
                        onChange={() => setLinkOption('wbs')}
                        label={'Link all activities under a specific WBS'}
                    />
                </div>

                <div className={classes.optionRow}>
                    <Radio
                        name={'linkOption'}
                        value={'activityCodes'}
                        checked={linkOption === 'activityCodes'}
                        onChange={() => setLinkOption('activityCodes')}
                        label={'Link all activities with a given Activity Code'}
                    />
                </div>

                <div className={classes.optionRow}>
                    <Radio
                        name={'linkOption'}
                        value={'cluster'}
                        checked={linkOption === 'cluster'}
                        onChange={() => setLinkOption('cluster')}
                        label={'Link all activities within a reference class cluster'}
                    />
                </div>

                <div className={classes.optionRow}>
                    <Radio
                        name={'linkOption'}
                        value={'segment'}
                        checked={linkOption === 'segment'}
                        onChange={() => setLinkOption('segment')}
                        label={'Link all activities within a segment'}
                    />
                </div>

                <div className={classes.optionRow}>
                    <Radio
                        name={'linkOption'}
                        value={'all'}
                        checked={linkOption === 'all'}
                        onChange={() => setLinkOption('all')}
                        label={'Link all project activities'}
                    />
                </div>
            </div>

            {hasInput && (
                <div
                    className={classes.inputColumn}
                    style={
                        {
                            '--selected-row-offset': `calc(${selectedRowIndex} * (var(--option-row-height) + var(--option-gap)))`,
                        } as React.CSSProperties
                    }
                >
                    <div
                        data-testid={'link-tasks-filter'}
                        className={`${classes.inputWrapper} ${classes.multiSelectInput} ${linkOption === 'specific' ? classes.visible : ''}`}
                    >
                        <Controller
                            render={() => (
                                <SpecificActivitiesSelect name={'activitiesByKey.specific'} methods={methods} />
                            )}
                            name={'activitiesByKey.specific'}
                            control={control}
                            rules={requiredWhenSelected(linkOption, 'specific', 'Select at least one activity')}
                        />
                    </div>

                    <div
                        data-testid={'link-wbs-filter'}
                        className={`${classes.inputWrapper} ${linkOption === 'wbs' ? classes.visible : ''}`}
                    >
                        <Controller
                            render={() => <WbsSelect name={'activitiesByKey.wbs'} methods={methods} />}
                            name={'activitiesByKey.wbs'}
                            control={control}
                            rules={requiredWhenSelected(linkOption, 'wbs', 'Select a WBS')}
                        />
                    </div>

                    <div
                        data-testid={'link-activity-filter'}
                        className={`${classes.inputWrapper} ${classes.multiSelectInput} ${linkOption === 'activityCodes' ? classes.visible : ''}`}
                    >
                        <Controller
                            render={() => (
                                <ActivityCodesSelect name={'activitiesByKey.activityCodes'} methods={methods} />
                            )}
                            name={'activitiesByKey.activityCodes'}
                            control={control}
                            rules={requiredWhenSelected(
                                linkOption,
                                'activityCodes',
                                'Select at least one activity code',
                            )}
                        />
                    </div>

                    <div
                        data-testid={'link-cluster-filter'}
                        className={`${classes.inputWrapper} ${linkOption === 'cluster' ? classes.visible : ''}`}
                    >
                        <Controller
                            render={() => <ClusterSelect name={'activitiesByKey.cluster'} methods={methods} />}
                            name={'activitiesByKey.cluster'}
                            control={control}
                            rules={requiredWhenSelected(linkOption, 'cluster', 'Select a cluster')}
                        />
                    </div>

                    <div
                        data-testid={'link-segment-filter'}
                        className={`${classes.inputWrapper} ${linkOption === 'segment' ? classes.visible : ''}`}
                    >
                        <Controller
                            render={() => <SegmentSelect name={'activitiesByKey.segment'} methods={methods} />}
                            name={'activitiesByKey.segment'}
                            control={control}
                            rules={requiredWhenSelected(linkOption, 'segment', 'Select a segment')}
                        />
                    </div>
                </div>
            )}

            {formError && (
                <div className={classes.formError}>
                    <CustomizedFormErrorMessage text={formError.message} />
                </div>
            )}
        </div>
    );
};
